import { useState } from 'react';
import Reveal from '@/components/base/Reveal';
import { trendingItems, trendingTags } from '@/mocks/homeData';

type TabKey = 'all' | 'hot' | 'new';

const tabs: { key: TabKey; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'hot', label: '热榜' },
  { key: 'new', label: '新瓜' },
];

const rankStyle: Record<number, string> = {
  1: 'bg-primary-500 text-background-50',
  2: 'bg-accent-500 text-background-50',
  3: 'bg-secondary-500 text-background-50',
};

export default function TrendingList() {
  const [tab, setTab] = useState<TabKey>('all');

  const list = tab === 'all' ? trendingItems : trendingItems.filter((item) => item.type === tab);

  return (
    <section id="trending" className="w-full scroll-mt-24 bg-background-50">
      <div className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
        <Reveal>
          <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <span className="text-xs font-label tracking-[0.2em] text-primary-600">TRENDING NOW</span>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">今日热议榜单</h2>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                按讨论热度实时排序，标题点进去就是完整复盘，评论区的段子往往比瓜本身还精彩。
              </p>
            </div>

            <div className="inline-flex items-center gap-1 self-start rounded-full border border-background-200 bg-background-100 px-1 py-1 md:self-auto">
              {tabs.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setTab(item.key)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    tab === item.key
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-950'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="flex flex-col gap-3 lg:col-span-2">
            {list.map((item, index) => (
              <Reveal key={item.title} delay={index * 50}>
                <article className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:bg-background-100 md:p-5">
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md font-heading text-sm font-bold ${
                      rankStyle[item.rank] ?? 'bg-background-200 text-foreground-700'
                    }`}
                  >
                    {item.rank}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-heading text-base font-bold leading-snug text-foreground-950">{item.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.summary}</p>
                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-fire-line text-primary-500" />
                        热度 {item.heat}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-chat-3-line text-secondary-600" />
                        {item.comments} 条讨论
                      </span>
                    </div>
                  </div>
                  <a
                    href="#discussion"
                    className="hidden shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-xs text-foreground-700 transition-colors hover:bg-background-200 sm:inline-flex"
                  >
                    去围观
                    <i className="ri-arrow-right-line" />
                  </a>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={120}>
            <aside className="flex flex-col gap-4 lg:sticky lg:top-32">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h3 className="font-heading text-base font-bold text-foreground-950">热议标签</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {trendingTags.map((tag) => (
                    <span key={tag} className="rounded-full border border-secondary-200 bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
                <h3 className="flex items-center gap-2 font-heading text-base font-bold text-accent-900">
                  <i className="ri-trophy-line" />
                  今日榜单快报
                </h3>
                <div className="mt-4 flex flex-col gap-3">
                  <div className="flex items-center justify-between border-b border-accent-200/70 pb-3 text-sm">
                    <span className="text-foreground-700">榜单更新</span>
                    <span className="font-medium text-foreground-950">每 30 分钟</span>
                  </div>
                  <div className="flex items-center justify-between border-b border-accent-200/70 pb-3 text-sm">
                    <span className="text-foreground-700">今日新增</span>
                    <span className="font-medium text-foreground-950">124 条</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-foreground-700">参与讨论</span>
                    <span className="font-medium text-foreground-950">2.1 万人</span>
                  </div>
                </div>
              </div>
            </aside>
          </Reveal>
        </div>
      </div>
    </section>
  );
}